import "isomorphic-unfetch"
import {v4 as uuidv4} from "uuid"
import {BaseAvroRecord} from "@videoblocks/events-ts/lib/BaseAvroRecord"
import EventParser from "./EventParser"
import RestProxyError from "./RestProxyError"
import AvroError from "./AvroError"
import {EventFactory} from "@videoblocks/events-ts/lib/util/EventFactory"
import {Origin} from "@videoblocks/events-ts/lib/storyblocks/common/OriginEnum"
import {FailedRecord} from "@videoblocks/events-ts/lib/storyblocks/common/FailedRecord"
import btoa = require("btoa-lite")

type RecordEntry = {key: string; value: object}
type FailedRecordEntry = {key: string; record: BaseAvroRecord; details: string}

export default class Client {
  private readonly restProxyUrl: string
  private readonly credentials?: {username: string; password: string}

  constructor(restProxyUrl: string, private readonly origin: Origin, private readonly encode = true, private readonly referer?: string) {
    this.restProxyUrl = this.cleanUrl(restProxyUrl)
    this.credentials = this.extractCredentials(restProxyUrl)
  }

  public async produce(record: BaseAvroRecord, key?: string, retries = 1) {
    const topic = EventParser.getTopic(record)
    const schema = EventParser.getSchema(record)

    try {
      const value = EventParser.parseValue(record)
      await this.postSingle(topic, schema, value, key)
    } catch (err) {
      if (retries > 0 && err instanceof RestProxyError) {
        await this.produce(record, key, retries - 1)
      } else if (err instanceof AvroError || err instanceof RestProxyError) {
        await this.produceFailedRecord(record, err.message, key)
      } else {
        throw err
      }
    }
  }

  /**
   * Keys are not currently supported for productMany(), mostly for convenience of client
   */
  public async produceMany(events: BaseAvroRecord[], retries = 1) {
    if (events.length < 1 || events.length > 1000) {
      throw new Error(`Client.productMany expects between 1-1000 events. ${events.length} events passed in.`)
    }

    const firstRecord = events[0]
    const topic = EventParser.getTopic(firstRecord)
    const schema = EventParser.getSchema(firstRecord)

    const validRecords: RecordEntry[] = []
    const invalidRecords: FailedRecordEntry[] = []

    events.forEach(record => {
      if (firstRecord.schema().name !== record.schema().name) {
        throw new Error(`Client.produceMany expects all objects to be of the same type. Expected ${firstRecord.schema().name} got ${record.schema().name}`)
      }

      const key = uuidv4()

      try {
        const value = EventParser.parseValue(record)
        validRecords.push({key, value})
      } catch (err) {
        invalidRecords.push({key, record, details: (err && err.message) || "Unknown"})
      }
    })

    if (invalidRecords.length) {
      await this.produceManyFailedRecords(invalidRecords)
    }

    if (!validRecords.length) {
      throw new Error("No valid records passed to produceMany")
    }

    try {
      await this.post(topic, schema, validRecords)
    } catch (err) {
      if (retries > 0 && err instanceof RestProxyError && err.status !== 422) {
        await this.produceMany(events, retries - 1)
      } else {
        throw err
      }
    }
  }

  public getOrigin(): Origin {
    return this.origin
  }

  private async produceFailedRecord(record: BaseAvroRecord, details: string, key?: string): Promise<void> {
    const failedRecord = EventFactory.failedRecord(record, EventParser.getTopic(record), this.origin, details)
    const topic = EventParser.getInvalidTopic(record)
    const schema = EventParser.getSchema(failedRecord)
    const value = EventParser.parseValue(failedRecord)
    await this.postSingle(topic, schema, value, key)
  }

  private async produceManyFailedRecords(records: FailedRecordEntry[]): Promise<void> {
    const topic = EventParser.getInvalidTopic(records[0].record)
    const schema = JSON.stringify(FailedRecord.schema)

    const failedRecords = records.map(({key, record, details}) => {
      const failedRecord = EventFactory.failedRecord(record, EventParser.getTopic(record), this.origin, details)
      const value = EventParser.parseValue(failedRecord)
      return {key, value}
    })

    await this.post(topic, schema, failedRecords)
  }

  private async postSingle(topic: string, schema: string, value: object, key?: string): Promise<void> {
    const records = [
      {
        key: key || uuidv4(),
        value
      }
    ]

    return await this.post(topic, schema, records)
  }

  private async post(topic: string, schema: string, records: object[]): Promise<void> {
    const uri = this.restProxyUrl + "/topics/" + topic

    const headers: HeadersInit = {}

    if (this.credentials) {
      // Note: This does not work for browsers that strip headers from pre-flight requests and servers which require Authorization for pre-flight (OPTIONS) requests.
      headers["Authorization"] = "Basic " + btoa(this.credentials.username + ":" + this.credentials.password)
    }

    if (this.referer) {
      headers["Referer"] = this.referer
    }

    let body = JSON.stringify({
      key_schema: '{"type": "string"}',
      value_schema: schema,
      records: records
    })

    if (this.encode) {
      headers["Content-Type"] = "text/plain"
      body = btoa(body)
    } else {
      headers["Content-Type"] = "application/vnd.kafka.avro.v2+json"
    }

    const response = await fetch(uri, {
      method: "POST",
      headers,
      body
    })

    if (response.status !== 200) {
      throw new RestProxyError(response)
    }
  }

  private cleanUrl(url: string): string {
    if (!url.startsWith("http")) {
      url = "http://" + url.replace(/^\/+/, "")
    }

    // Remove basic auth if it exists.
    url = url.replace(/\w+?:\w+?@/, "")

    return url
  }

  private extractCredentials(url: string) {
    // Match groups: protocol, username, password, url
    const regex = /^(.+?\/\/)(.+?):(.+?)@(.+)$/
    const matches = regex.exec(url)

    if (matches) {
      const [_, protocol, username, password] = matches
      return {username, password}
    }
  }
}
